import useExtendedFormContext from '@components/form-hook-wrapper/hooks/useExtendedFormContext';
import { replaceAllowCharacter } from '@helpers/@utils/replace-allow-characters';
import { Input } from '@mis/sushi-tailwind-react';
import { FieldController } from '@model/field-controller';
import React from 'react';
import { Controller, FieldPath, FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export type InputStringProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> = FieldController<TFieldValues, TName> &
  React.ComponentProps<typeof Input> & { unlimitedLength?: boolean; showErrorDisabled?: boolean; allowOnly?: 'en' | 'th' };

const InputString = <TFieldValues extends FieldValues = FieldValues, TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>>({
  name,
  rules,
  unlimitedLength,
  showErrorDisabled,
  allowOnly,
  disabled,
  viewOnly,
  placeholder,
  ...props
}: InputStringProps<TFieldValues, TName>): React.ReactElement => {
  const { t } = useTranslation('error');
  const { control, displayErrorMessage } = useExtendedFormContext<TFieldValues>();

  const onChangeValue = (onChange: (...e: AnyValue[]) => void) => (e: React.ChangeEvent<HTMLInputElement>) => {
    if (allowOnly && e.target.value) e = replaceAllowCharacter(e, allowOnly);
    onChange?.(e);
  };

  return (
    <Controller
      name={name}
      rules={rules}
      control={control}
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      render={({ field: { ref, ...field }, fieldState: { error } }) => {
        const getDisplayMessageError = (val: string) => {
          const conditionDisabled = disabled || viewOnly ? showErrorDisabled : true;
          if (displayErrorMessage && conditionDisabled && error) return val;
          return undefined;
        };
        return (
          <Input
            {...field}
            id={name}
            viewOnly={viewOnly}
            disabled={!viewOnly && disabled}
            value={field.value === null ? '' : field.value}
            autoComplete="off"
            message={getDisplayMessageError(t(error?.message as string))}
            status={getDisplayMessageError('error') as 'error'}
            data-testid={name}
            maxLength={unlimitedLength ? undefined : 255}
            placeholder={!viewOnly ? placeholder : undefined}
            style={{ color: 'black' }}
            onChange={onChangeValue(field.onChange)}
            {...props}
          />
        );
      }}
    />
  );
};

export default InputString;
